import { t } from '@core/i18n';
import type { DashboardExternalLink } from '../types';
import React from 'react';
import { DashboardMenu, DashboardMenuItem, useDashboardMenu } from '../components/DashboardMenu';
import { dashboardCreateIntentHref } from '../utils/routeCreateIntent';
import { PlusIcon } from '../icons/SidebarIcons';
import DashboardSidebarToggleIcon from '../icons/DashboardSidebarToggleIcon';
import { getDocsOrigin } from '@core/router/siteRouting';
import SeamsWordmark from '@core/components/SeamsWordmark';
import { GrainField } from '@seams-internal/grain-field';
import type { DashboardProduct, DashboardProductId, SidebarGroup, TopbarOption } from '../types';
import { useDashboardConsoleSession } from '../consoleSession';
import { dashboardSignInLabel } from '../utils/userIdentity';
import { ProviderMark } from '@core/auth/ProviderMark';
import { AccountMenuToggles, type DashboardNetworkSwitch } from './AccountMenuToggles';

export type DashboardLinkPropsFactory = (to: string) => {
  href: string;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

export type DashboardHomeLinkProps = ReturnType<DashboardLinkPropsFactory>;

export type SidebarProjectGroup = {
  project: TopbarOption;
  environments: TopbarOption[];
};

export type DashboardWorkspaceNavigation = {
  projectGroups: SidebarProjectGroup[];
  projectValue: string;
  organizationOptions: TopbarOption[];
  organizationValue: string;
} & (
  | {
      kind: 'account';
      onSelectProject: (projectValue: string) => void;
      environmentValue?: never;
      onSelectEnvironment?: never;
    }
  | {
      kind: 'wallet';
      environmentValue: string;
      onSelectEnvironment: (projectValue: string, environmentValue: string) => void;
      onSelectProject?: never;
    }
);

export type SidebarWorkspaceProps = DashboardWorkspaceNavigation & {
  onSelectOrganization: (value: string) => void;
};

export type SidebarProductProps = {
  products: DashboardProduct[];
  currentId: DashboardProductId;
  onSelect: (id: DashboardProductId) => void;
};

export type DashboardSidebarProps<Route extends string, GroupKey extends string> = {
  accountLabel: string;
  accountOptions: TopbarOption[];
  network?: DashboardNetworkSwitch;
  onSelectAccount: (value: string) => void;
  groups: SidebarGroup<Route, GroupKey>[];
  externalLinks?: readonly DashboardExternalLink[];
  isSidebarExpanded: boolean;
  activeRoute: Route;
  disableNavigationItems?: boolean;
  enabledWhenLockedPaths?: ReadonlySet<Route>;
  onToggleSidebar: () => void;
  linkProps: DashboardLinkPropsFactory;
  product?: SidebarProductProps;
  workspace?: SidebarWorkspaceProps;
  homeProps?: DashboardHomeLinkProps;
};

/* The rail shows the project at rest and slides to the environment on hover,
   so the scope is one glance away without a second permanent line. */
function WorkspaceSwitcherLabel({
  projectLabel,
  environmentLabel,
}: {
  projectLabel: string;
  environmentLabel: string;
}): React.JSX.Element {
  return (
    <>
      <span className="dashboard-workspace-switcher__label" aria-hidden="true">
        <span className="dashboard-workspace-switcher__ticker">
          <span
            className="dashboard-workspace-switcher__ticker-row dashboard-workspace-switcher__ticker-row--project"
            title={projectLabel}
          >
            {projectLabel}
          </span>
          <span
            className="dashboard-workspace-switcher__ticker-row dashboard-workspace-switcher__ticker-row--environment"
            title={environmentLabel}
          >
            {environmentLabel}
          </span>
        </span>
      </span>
      <span className="dashboard-visually-hidden">
        {projectLabel}, {environmentLabel}
      </span>
    </>
  );
}

/* The rail scrolls (overflow-y: auto), which would clip an absolutely
   positioned popover to the rail's width. Fixed positioning escapes that clip
   so the menu can be wider than the rail, reference-app style. The trigger
   lives in the sticky head, so its viewport position is stable while open. */
function useRailMenuPosition(
  open: boolean,
  rootRef: React.RefObject<HTMLDivElement | null>,
): React.CSSProperties | undefined {
  const [style, setStyle] = React.useState<React.CSSProperties | undefined>(undefined);
  React.useLayoutEffect(() => {
    if (!open) {
      setStyle(undefined);
      return;
    }
    const root = rootRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    const width = Math.max(rect.width, Math.min(320, window.innerWidth - rect.left - 16));
    setStyle({
      position: 'fixed',
      top: rect.bottom + 6,
      left: rect.left,
      right: 'auto',
      width,
    });
  }, [open, rootRef]);
  return style;
}

function RailCaret({ className }: { className: string }): React.JSX.Element {
  return (
    <svg
      className={className}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m7 15 5 5 5-5" />
      <path d="m7 9 5-5 5 5" />
    </svg>
  );
}

/* One avatar row inside a switcher popover: avatar + name (+ description),
   with a trailing check on the active row or a "Soon" pill on unshipped
   products. Disabled rows keep their click as a no-op. */
function RailMenuRow({
  active,
  soon = false,
  disabled = false,
  avatar,
  name,
  description,
  onSelect,
}: {
  active: boolean;
  soon?: boolean;
  disabled?: boolean;
  avatar: React.ReactNode;
  name: string;
  description?: string;
  onSelect: () => void;
}): React.JSX.Element {
  const blocked = soon || disabled;
  return (
    <DashboardMenuItem
      role="menuitemradio"
      aria-checked={active}
      blocked={blocked}
      className={`dashboard-rail-menu__item${active ? ' is-active' : ''}${blocked ? ' is-soon' : ''}`}
      onSelect={onSelect}
    >
      {avatar}
      <span className="dashboard-rail-menu__item-text">
        <span className="dashboard-rail-menu__item-name">{name}</span>
        {description ? <span className="dashboard-rail-menu__item-desc">{description}</span> : null}
      </span>
      {soon ? (
        <span className="dashboard-rail-menu__soon">{t('commonSidebarSoon')}</span>
      ) : active ? (
        <svg
          className="dashboard-rail-menu__check"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
      ) : null}
    </DashboardMenuItem>
  );
}

function workspaceLetterAvatar(label: string): React.JSX.Element {
  return (
    <span
      className="dashboard-rail-menu__item-avatar dashboard-rail-menu__item-avatar--letter"
      aria-hidden="true"
    >
      {(label.trim().charAt(0) || 'W').toUpperCase()}
    </span>
  );
}

function renderAccountProject(
  props: Extract<SidebarWorkspaceProps, { kind: 'account' }>,
  currentProjectValue: string | null,
  group: SidebarProjectGroup,
): React.JSX.Element {
  return (
    <RailMenuRow
      key={group.project.value}
      active={group.project.value === currentProjectValue}
      disabled={group.project.disabled === true}
      avatar={workspaceLetterAvatar(group.project.label)}
      name={group.project.label}
      onSelect={props.onSelectProject.bind(null, group.project.value)}
    />
  );
}

/* Wallet routes select a project/environment pair. Account routes select a project. */
export function SidebarWorkspaceSwitcher(props: SidebarWorkspaceProps): React.JSX.Element {
  const {
    projectGroups,
    projectValue,
    organizationOptions,
    organizationValue,
    onSelectOrganization,
  } = props;
  const environmentValue = props.kind === 'wallet' ? props.environmentValue : '';
  const menu = useDashboardMenu();
  const menuStyle = useRailMenuPosition(menu.mounted, menu.rootRef);

  const currentProjectGroup =
    projectGroups.find((group) => group.project.value === projectValue) || projectGroups[0] || null;
  const currentProject = currentProjectGroup?.project || null;
  const currentEnvironment =
    currentProjectGroup?.environments.find((option) => option.value === environmentValue) || null;
  const currentOrganization =
    organizationOptions.find((option) => option.value === organizationValue) ||
    organizationOptions[0] ||
    null;
  /* Projects are the working scope; the organization label is only a fallback
     for accounts that have not created a project yet. */
  const currentLabel =
    currentProject?.label || currentOrganization?.label || t('commonSidebarWorkspace');
  const currentEnvironmentLabel =
    currentEnvironment?.label || environmentValue || t('commonNoEnvironment');
  const initial = (currentLabel.trim().charAt(0) || 'W').toUpperCase();
  const showOrganizations = organizationOptions.length > 1;
  const showSectionTitles = showOrganizations && projectGroups.length > 0;

  return (
    <div ref={menu.rootRef} className="dashboard-workspace-switcher">
      <button
        type="button"
        className="dashboard-workspace-switcher__trigger"
        aria-haspopup="menu"
        aria-expanded={menu.open}
        aria-label={
          props.kind === 'wallet' ? `${currentLabel}, ${currentEnvironmentLabel}` : currentLabel
        }
        onClick={menu.toggle}
      >
        <span className="dashboard-workspace-switcher__avatar" aria-hidden="true">
          {initial}
        </span>
        {props.kind === 'wallet' ? (
          <WorkspaceSwitcherLabel
            projectLabel={currentLabel}
            environmentLabel={currentEnvironmentLabel}
          />
        ) : (
          <span className="dashboard-workspace-switcher__label">{currentLabel}</span>
        )}
        <RailCaret className="dashboard-workspace-switcher__caret" />
      </button>
      <DashboardMenu
        menu={menu}
        className="dashboard-context-menu dashboard-rail-menu"
        style={menuStyle}
        aria-label={t('commonSidebarWorkspace')}
      >
        {showSectionTitles ? (
          <p className="dashboard-rail-menu__section-title">{t('commonSidebarProjects')}</p>
        ) : null}
        {props.kind === 'account'
          ? projectGroups.map(renderAccountProject.bind(null, props, currentProject?.value || null))
          : projectGroups.map((group) => (
              <div
                key={group.project.value}
                className="dashboard-workspace-menu__project"
                role="group"
                aria-label={group.project.label}
              >
                <div className="dashboard-workspace-menu__project-heading">
                  {workspaceLetterAvatar(group.project.label)}
                  <span>{group.project.label}</span>
                </div>
                <div className="dashboard-workspace-menu__environments">
                  {group.environments.map((environment) => {
                    const active =
                      group.project.value === currentProject?.value &&
                      environment.value === environmentValue;
                    const disabled =
                      group.project.disabled === true || environment.disabled === true;
                    return (
                      <DashboardMenuItem
                        key={environment.value}
                        role="menuitemradio"
                        aria-checked={active}
                        blocked={disabled}
                        className={`dashboard-workspace-menu__environment${active ? ' is-active' : ''}${disabled ? ' is-disabled' : ''}`}
                        onSelect={props.onSelectEnvironment.bind(
                          null,
                          group.project.value,
                          environment.value,
                        )}
                      >
                        <span
                          className="dashboard-workspace-menu__environment-dot"
                          aria-hidden="true"
                        />
                        <span>{environment.label}</span>
                        {active ? (
                          <svg
                            className="dashboard-rail-menu__check"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        ) : null}
                      </DashboardMenuItem>
                    );
                  })}
                  {group.environments.length === 0 ? (
                    <p className="dashboard-workspace-menu__empty">
                      {t('commonSidebarNoEnvironments')}
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
        {projectGroups.length === 0 && !showOrganizations ? (
          <p className="dashboard-rail-menu__empty">{t('commonSidebarNoProjects')}</p>
        ) : null}
        {showOrganizations ? (
          <>
            <p className="dashboard-rail-menu__section-title">{t('commonSidebarOrganizations')}</p>
            {organizationOptions.map((option) => (
              <RailMenuRow
                key={option.value}
                active={option.value === currentOrganization?.value}
                disabled={option.disabled === true}
                avatar={workspaceLetterAvatar(option.label)}
                name={option.label}
                onSelect={() => onSelectOrganization(option.value)}
              />
            ))}
          </>
        ) : null}
      </DashboardMenu>
    </div>
  );
}

/* Product-line switcher pinned at the sidebar top, reference-app style: a
   single-line gradient avatar + product name + up/down chevron, opening a
   menu of products each with a short description. Not-yet-shipped products
   render disabled with a "Soon" pill. */
function SidebarProductSwitcher({
  products,
  currentId,
  onSelect,
}: SidebarProductProps): React.JSX.Element {
  const menu = useDashboardMenu();
  const menuStyle = useRailMenuPosition(menu.mounted, menu.rootRef);
  const current = products.find((product) => product.id === currentId) || products[0];

  if (!current) return <></>;

  return (
    <div ref={menu.rootRef} className="dashboard-product-switcher">
      <button
        type="button"
        className="dashboard-product-switcher__trigger"
        aria-haspopup="menu"
        aria-expanded={menu.open}
        onClick={menu.toggle}
      >
        <span className="dashboard-product-switcher__avatar" aria-hidden="true">
          <GrainField field={current.gradient} playing={false} />
        </span>
        <span className="dashboard-product-switcher__label">{current.name}</span>
        <RailCaret className="dashboard-product-switcher__caret" />
      </button>
      <DashboardMenu
        menu={menu}
        className="dashboard-context-menu dashboard-rail-menu"
        style={menuStyle}
        aria-label={t('commonSidebarProducts')}
      >
        {products.map((product) => (
          <RailMenuRow
            key={product.id}
            active={product.id === currentId}
            soon={!product.available}
            avatar={
              <span
                className="dashboard-rail-menu__item-avatar dashboard-rail-menu__item-avatar--image"
                aria-hidden="true"
              >
                <GrainField field={product.gradient} playing={false} />
              </span>
            }
            name={product.name}
            description={product.description}
            onSelect={() => onSelect(product.id)}
          />
        ))}
      </DashboardMenu>
    </div>
  );
}

const MOBILE_NAVIGATION_QUERY = '(max-width: 1160px)';

function subscribeMobileNavigation(callback: () => void): () => void {
  const query = window.matchMedia(MOBILE_NAVIGATION_QUERY);
  query.addEventListener('change', callback);
  return removeMobileNavigationListener.bind(null, query, callback);
}

function removeMobileNavigationListener(query: MediaQueryList, callback: () => void): void {
  query.removeEventListener('change', callback);
}

function isMobileNavigation(): boolean {
  return window.matchMedia(MOBILE_NAVIGATION_QUERY).matches;
}

function serverMobileNavigation(): boolean {
  return false;
}

export function openMobileNavigation(): void {
  const dialog = document.getElementById('dashboard-mobile-navigation');
  if (dialog instanceof HTMLDialogElement) dialog.showModal();
}

function closeMobileNavigation(): void {
  const dialog = document.getElementById('dashboard-mobile-navigation');
  if (dialog instanceof HTMLDialogElement) dialog.close();
}

function dismissMobileNavigationLink(event: React.MouseEvent<HTMLDialogElement>): void {
  if (
    !(event.target instanceof Element) ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  )
    return;
  const link = event.target.closest('a[href]');
  if (link && link.getAttribute('aria-disabled') !== 'true') closeMobileNavigation();
}

function selectMobileAccountOption(onSelect: (value: string) => void, option: TopbarOption): void {
  if (option.keepMenuOpen !== true) closeMobileNavigation();
  onSelect(option.value);
}

function ExternalConsoleLink({
  link,
}: {
  readonly link: DashboardExternalLink;
}): React.JSX.Element {
  const Icon = link.icon;
  return (
    <li>
      <a className="dashboard-nav-item" href={link.href}>
        <span className="dashboard-nav-icon" aria-hidden="true">
          <Icon size={20} />
        </span>
        <span className="dashboard-nav-label">{link.label}</span>
      </a>
    </li>
  );
}

function externalConsoleLink(link: DashboardExternalLink): React.JSX.Element {
  return <ExternalConsoleLink key={link.href} link={link} />;
}

export function DashboardSidebar<Route extends string, GroupKey extends string>({
  accountLabel,
  accountOptions,
  network,
  onSelectAccount,
  groups,
  externalLinks = [],
  isSidebarExpanded,
  activeRoute,
  disableNavigationItems = false,
  enabledWhenLockedPaths,
  onToggleSidebar,
  linkProps,
  product,
  workspace,
  homeProps,
}: DashboardSidebarProps<Route, GroupKey>): React.JSX.Element {
  const session = useDashboardConsoleSession();
  const signInLabel = dashboardSignInLabel(session.claims?.provider ?? null);
  const mobile = React.useSyncExternalStore(
    subscribeMobileNavigation,
    isMobileNavigation,
    serverMobileNavigation,
  );
  const content = (
    <aside
      id="dashboard-sidebar-navigation"
      className="dashboard-sidebar"
      aria-label={t('commonSidebarPrimaryNavigation')}
    >
      {/* Pinned head: wordmark + product/org switchers stay fixed at the sidebar
          top (reference-app style) while the nav list scrolls beneath them. */}
      {homeProps || product || workspace ? (
        <div className="dashboard-sidebar__head">
          <div className="dashboard-sidebar__brand-row">
            {homeProps ? (
              <a
                className="dashboard-home-link dashboard-sidebar__brand"
                href={homeProps.href}
                onClick={homeProps.onClick}
                aria-label={t('loginAriaHome')}
              >
                <SeamsWordmark height={18} />
              </a>
            ) : null}
            {mobile || isSidebarExpanded ? (
              <button
                type="button"
                className="dashboard-sidebar-toggle dashboard-sidebar-toggle--rail"
                aria-label={mobile ? t('commonSidebarCloseNavigation') : t('commonSidebarCollapse')}
                aria-expanded="true"
                aria-controls={
                  mobile ? 'dashboard-mobile-navigation' : 'dashboard-sidebar-navigation'
                }
                onClick={mobile ? closeMobileNavigation : onToggleSidebar}
              >
                {mobile ? (
                  <span aria-hidden="true">×</span>
                ) : (
                  <DashboardSidebarToggleIcon expanded />
                )}
              </button>
            ) : null}
          </div>
          {mobile ? (
            <p className="dashboard-mobile-section-label">{t('commonSidebarProject')}</p>
          ) : null}
          {mobile && workspace ? <SidebarWorkspaceSwitcher {...workspace} /> : null}
          {product ? <SidebarProductSwitcher {...product} /> : null}
          {!mobile && workspace ? <SidebarWorkspaceSwitcher {...workspace} /> : null}
        </div>
      ) : null}
      {groups.map((group, groupIndex) => (
        <section className="dashboard-sidebar-group" key={group.key}>
          {/* First section is header-less, reference-app style; the rest get
              static muted labels (no collapse affordance). */}
          {groupIndex > 0 ? <p className="dashboard-sidebar-group__title">{group.label}</p> : null}
          <ul className="dashboard-nav-list">
            {group.items.map((item) => {
              const ItemIcon = item.icon;
              const navProps = linkProps(item.path);
              const isActive = item.path === activeRoute;
              const isDisabled = disableNavigationItems && !enabledWhenLockedPaths?.has(item.path);
              return (
                <li key={item.key}>
                  <a
                    className={`dashboard-nav-item${isActive ? ' dashboard-nav-item--active' : ''}${isDisabled ? ' dashboard-nav-item--disabled' : ''}`}
                    href={navProps.href}
                    onClick={
                      isDisabled
                        ? (event) => {
                            event.preventDefault();
                          }
                        : navProps.onClick
                    }
                    aria-current={isActive ? 'page' : undefined}
                    aria-disabled={isDisabled || undefined}
                    tabIndex={isDisabled ? -1 : undefined}
                  >
                    <span className="dashboard-nav-icon" aria-hidden="true">
                      <ItemIcon size={20} />
                    </span>
                    <span className="dashboard-nav-label">{item.label}</span>
                  </a>
                  {item.createLabel && !isDisabled ? (
                    <button
                      type="button"
                      className="dashboard-nav-create"
                      aria-label={item.createLabel}
                      title={item.createLabel}
                      onClick={(event) => {
                        event.preventDefault();
                        linkProps(dashboardCreateIntentHref(item.path)).onClick(
                          event as unknown as React.MouseEvent<HTMLAnchorElement>,
                        );
                      }}
                    >
                      <PlusIcon size={14} />
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      {externalLinks.length > 0 ? (
        <section className="dashboard-sidebar-group">
          <p className="dashboard-sidebar-group__title">
            {product?.currentId === 'platform' ? t('commonSidebarProducts') : t('navPlatform')}
          </p>
          <ul className="dashboard-nav-list">{externalLinks.map(externalConsoleLink)}</ul>
        </section>
      ) : null}
      {mobile ? (
        <div className="dashboard-mobile-support">
          <a href={getDocsOrigin()} target="_blank" rel="noreferrer">
            {t('loginDocumentation')} <span aria-hidden="true">↗</span>
          </a>
          <details className="dashboard-mobile-account">
            <summary>
              <span className="dashboard-account-menu__avatar" aria-hidden="true">
                {accountLabel.charAt(0).toUpperCase()}
              </span>
              {accountLabel}
            </summary>
            <div>
              {signInLabel ? (
                <div className="dashboard-account-menu__identity">
                  <span className="dashboard-sign-in-method dashboard-account-menu__sign-in">
                    <ProviderMark provider={session.claims?.provider ?? null} />
                    {signInLabel}
                  </span>
                </div>
              ) : null}
              <AccountMenuToggles network={network} inMenu={false} />
              {accountOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={option.disabled}
                  onClick={selectMobileAccountOption.bind(null, onSelectAccount, option)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </details>
        </div>
      ) : null}
    </aside>
  );
  return mobile ? (
    <dialog
      id="dashboard-mobile-navigation"
      className="dashboard-mobile-navigation"
      aria-label={t('commonSidebarNavigation')}
      onClick={dismissMobileNavigationLink}
    >
      {content}
    </dialog>
  ) : (
    content
  );
}

export default DashboardSidebar;
